// src/pages/DebtStudio/components/Reports/StatsReport.jsx

import React, { useMemo } from "react";
import { useDebtStudio } from "pages/DebtStudio/context/DebtStudioContext";
import { buildIssueStats } from "pages/DebtStudio/lib/buildIssueStats";
import { buildRefundingStats } from "pages/DebtStudio/lib/buildRefundingStats";
import dayjs from "lib/dayjs";
import { fmtPct3, fmtPct6 } from "pages/DebtStudio/utils/formatters";

/** ---------- Formatters ---------- */
const fmtUSD = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) =>
  d && dayjs(d).isValid() ? dayjs(d).format("MM/DD/YYYY") : "—";

/** ---------- Component ---------- */
export default function StatsReport({ bondRows = [], flowRows = [], refundingComparison = [] }) {
  const { form } = useDebtStudio();

  const { datedDate, deliveryDate } = form || {};

  const isLoan =
    String(form?.financingType || "").trim().toLowerCase() === "loan";

  const stats = useMemo(
    () =>
      buildIssueStats({
        form,
        bondRows,
        flowRows,
      }),
    [form, bondRows, flowRows]
  );

  const refundingStats = useMemo(() => buildRefundingStats({
    form,
    bondRows,
    comparison: refundingComparison,
    discountRate: stats?.arbYield,
  }), [form, bondRows, refundingComparison, stats?.arbYield]);

  if (!stats) return <div className="p-4 text-muted">No data available.</div>;

  const {
    totalPrincipal,
    totalInterest,
    totalDebtService,
    firstPayment,
    lastPayment,
    wal,
    macaulayDuration,
    avgAnnualDS,
    maxAnnualDS,
    hasBalloon,
    balloonPrincipal,
    tic,
    nic,
    aic,
    wac,
    wap,
    arbYield,
    pv01,
  } = stats;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
      {String(form?.dealType || "").toLowerCase() === "refunding" && (
        <section className="md:col-span-2 p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
          <h3 className="font-semibold mb-2 text-app">Refunding Results</h3>
          {refundingStats ? (
            <div className="space-y-1 max-w-2xl">
              <div className="flex justify-between"><span>Prior Debt Service</span><span>{fmtUSD(refundingStats.priorDebtService)}</span></div>
              <div className="flex justify-between"><span>Refunding Debt Service</span><span>{fmtUSD(refundingStats.refundingDebtService)}</span></div>
              <div className="flex justify-between"><span>Gross Cash-Flow Savings</span><span>{fmtUSD(refundingStats.grossCashFlowSavings)}</span></div>
              <div className="flex justify-between gap-4 font-semibold"><span>Net PV Savings / Savings % (preliminary)</span><span>{refundingStats.preliminaryNetPvSavings == null ? "—" : `${fmtUSD(refundingStats.preliminaryNetPvSavings)} / ${fmtPct3(refundingStats.preliminarySavingsPctOfRefundedPar)}`}</span></div>
              <p className="pt-2 text-xs text-muted">PV discounted to delivery at bond yield of {refundingStats.discountRate == null ? "—" : fmtPct6(refundingStats.discountRate)}.</p>
              <p className="text-xs text-muted">Preliminary net assumes zero prior/refunding funds-on-hand adjustments and excludes escrow effects.</p>
            </div>
          ) : (
            <div className="text-muted">Select refunded DebtStudio maturities and solve the refunding to see results.</div>
          )}
        </section>
      )}
      {/* General */}
      <section className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
        <h3 className="font-semibold mb-2 text-app">General</h3>

        <div className="space-y-1">
          <div className="flex justify-between">
            <span>{isLoan ? "Loan Date" : "Dated Date"}</span>
            <span>{fmtDate(datedDate)}</span>
          </div>

          {!isLoan && (
            <div className="flex justify-between">
              <span>Delivery Date</span>
              <span>{fmtDate(deliveryDate)}</span>
            </div>
          )}

          <div className="flex justify-between">
            <span>First Payment</span>
            <span>{fmtDate(firstPayment)}</span>
          </div>

          <div className="flex justify-between">
            <span>Final Maturity</span>
            <span>{fmtDate(lastPayment)}</span>
          </div>
        </div>
      </section>

      {/* Issue Metrics */}
      <section className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
        <h3 className="font-semibold mb-2 text-app">
          {isLoan ? "Loan Metrics" : "Issue Metrics"}
        </h3>

        <div className="space-y-1">
          <div className="flex justify-between">
            <span>
              {isLoan ? "Weighted Avg Interest Rate" : "Average Coupon"}
            </span>
            <span>{fmtPct3(wac)}</span>
          </div>

          {!isLoan && (
            <div className="flex justify-between">
              <span>Weighted Avg Price</span>
              <span>{Number(wap || 0).toFixed(9)}</span>
            </div>
          )}

          <div className="flex justify-between">
            <span>Weighted Average Life</span>
            <span>{Number(wal || 0).toFixed(4)} yrs</span>
          </div>

          {!isLoan && (
            <>
              <div className="flex justify-between">
                <span>Duration (Macaulay)</span>
                <span>
                  {macaulayDuration == null
                    ? "—"
                    : `${Number(macaulayDuration).toFixed(4)} yrs`}
                </span>
              </div>

              <div className="flex justify-between">
                <span>PV01</span>
                <span>{pv01 == null ? "—" : fmtUSD(pv01)}</span>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Debt Service */}
      <section className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
        <h3 className="font-semibold mb-2 text-app">
          Debt Service Summary
        </h3>

        <div className="space-y-1">
          <div className="flex justify-between">
            <span>Total Principal</span>
            <span>{fmtUSD(totalPrincipal)}</span>
          </div>

          <div className="flex justify-between">
            <span>Total Interest</span>
            <span>{fmtUSD(totalInterest)}</span>
          </div>

          <div className="flex justify-between font-semibold">
            <span>Total Debt Service</span>
            <span>{fmtUSD(totalDebtService)}</span>
          </div>

          <div className="flex justify-between">
            <span>
              {isLoan
                ? "Avg Annual Debt Service (Recurring)"
                : "Avg Annual Debt Service"}
            </span>
            <span>{fmtUSD(avgAnnualDS)}</span>
          </div>

          {!isLoan && (
            <div className="flex justify-between">
              <span>Max Annual Debt Service</span>
              <span>{fmtUSD(maxAnnualDS)}</span>
            </div>
          )}

          {isLoan && hasBalloon && (
            <>
              <div className="flex justify-between">
                <span>Balloon Principal (at Maturity)</span>
                <span>{fmtUSD(balloonPrincipal)}</span>
              </div>
              <p className="pt-2 text-xs text-muted">
                Recurring AADS excludes Balloon Principal.
              </p>
            </>
          )}
        </div>
      </section>

      {/* Yield Metrics */}
      {!isLoan && (
        <section className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
          <h3 className="font-semibold mb-2 text-app">Yield Metrics</h3>

          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Arbitrage Yield</span>
              <span>{arbYield == null ? "—" : fmtPct6(arbYield)}</span>
            </div>

            <div className="flex justify-between">
              <span>True Interest Cost</span>
              <span>{tic == null ? "—" : fmtPct6(tic)}</span>
            </div>

            <div className="flex justify-between">
              <span>Net Interest Cost</span>
              <span>{nic == null ? "—" : fmtPct6(nic)}</span>
            </div>

            <div className="flex justify-between">
              <span>All-In Cost</span>
              <span>{aic == null ? "—" : fmtPct6(aic)}</span>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
